import React from 'react';
import classnames from 'classnames';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import {
  IntlProps,
  addTranslation,
} from '../../../../decorators/addTranslation';
import Icon from 'components/ui/icon';

import './multipleFileUpload.scss';
import { ACCEPTED_FILES_CONFIG } from './constants';

type OwnProps = {
  selectedFiles: Map<string, File>;
  onDrop: DropzoneOptions['onDrop'];
  unselectFile: (id: string) => void;
  maxFileSize?: number;
  disabled?: boolean;
  fileTypes?: string;
  accept?: DropzoneOptions['accept'];
};

type Props = IntlProps & OwnProps;

// TODO: move to utils
const getFormatIcon = (fileName: string) => {
  const type = fileName.split('.').pop();

  switch (type) {
    case 'csv':
      return 'csv';
    case 'xlsx':
    case 'xlsm':
    case 'xlsb':
    case 'xltx':
      return 'excel';
    case 'docx':
    case 'doc':
      return 'word';
    case 'jpeg':
    case 'jpg':
    case 'png':
    case 'svg':
      return 'images';
    case 'pdf':
      return 'pdf';
    case 'pptx':
    case 'pptm':
    case 'ppt':
      return 'power-point';
    default:
      return 'unknown';
  }
};

const IconTypeIcon = ({ type }: { type: ReturnType<typeof getFormatIcon> }) => (
  <img src={require(`images/fileFormats/${type}.svg`)} alt='file-icon' />
);

const Component = ({
  onDrop,
  selectedFiles,
  unselectFile,
  maxFileSize,
  disabled,
  fileTypes,
  accept,
}: Props) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: accept || ACCEPTED_FILES_CONFIG,
  });

  const getFileSizeText = (size: number) => {
    const sizeInKb = size / 1024;
    if (sizeInKb < 1024) return `${sizeInKb.toFixed(1)} KB`;

    return `${(sizeInKb / 1024).toFixed(1)} MB`;
  };

  const rootProps = disabled ? {} : getRootProps();
  const inputProps = disabled ? {} : getInputProps({ accept: fileTypes });

  return (
    <>
      <div
        {...rootProps}
        className={classnames('muliple-file-upload', {
          'muliple-file-upload_disabled': disabled,
        })}>
        {!disabled && (
          <div className='muliple-file-upload__info-block'>
            <Icon
              name='im-Upload'
              className='muliple-file-upload__icon'
              size={16}
            />
            <p>
              Drop your documents here, or{' '}
              <span className='muliple-file-upload__link-text'>
                click to browse
              </span>
            </p>
          </div>
        )}
        <input {...inputProps} />
        <div className='muliple-file-upload__selected-files'>
          {[...selectedFiles.entries()].map(([id, { name, size }]) => (
            <div
              className={classnames('selected-file', {
                'selected-file_disabled': disabled,
              })}
              key={id}>
              <div className='selected-file__icon'>
                <IconTypeIcon type={getFormatIcon(name)} />
              </div>
              <div className='selected-file__name'>{name}</div>
              {size && (
                <div className='selected-file__size'>
                  {/* TODO calculation to function */}
                  {getFileSizeText(size)}
                </div>
              )}
              {!disabled && (
                <div
                  className='selected-file__unselect-button'
                  onClick={(event) => {
                    unselectFile(id);
                    event.stopPropagation();
                  }}>
                  <Icon name='im-Basket' size={11} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {!disabled && (
        <div className='file-size-info'>{`Max file size: ${maxFileSize} MB`}</div>
      )}
    </>
  );
};

export const MultipleFileUploadView = addTranslation(Component);
